// Señal de "esto es una recuperación de contraseña", latcheada al arrancar la app.
//
// POR QUÉ VIVE ACÁ Y NO EN LA PÁGINA DE RESET (los dos motivos son de timing real, no teóricos):
//
//  1) `supabase-js` emite `PASSWORD_RECOVERY` desde un `setTimeout(..., 0)` dentro de su
//     `initialize()`, que arranca en el constructor de `createClient`. La página de reset se carga
//     como chunk lazy (`React.lazy` en Routes.jsx) y monta MUCHO después de ese tick: un listener
//     montado en el componente se pierde el evento de forma sistemática.
//
//  2) Cuando el token del hash es válido, `_getSessionFromURL` hace `window.location.hash = ""`.
//     Para poder distinguir "el usuario llegó con un enlace de recuperación" de "el navegador ya
//     tenía una sesión previa" hay que leer los parámetros ANTES de ese borrado.
//
// Por eso `supabaseClient.js` instala esta señal en el mismo tick en que crea el cliente.
//
// Nunca se guarda ni se expone el token: sólo metadatos (¿es callback?, ¿es recovery?, ¿hay error?).

/**
 * Lee los metadatos del callback de Auth desde una URL, mirando hash Y query
 * (implicit deja los datos en el hash; PKCE y los errores pueden venir en cualquiera de los dos).
 *
 * Devuelve SOLO metadatos — jamás `access_token`, `refresh_token` ni `code`.
 */
export function parseAuthCallback(href) {
  const empty = { isAuthCallback: false, isRecovery: false, hasError: false, errorCode: null };
  if (!href) return empty;

  let url;
  try {
    url = new URL(href);
  } catch {
    return empty;
  }

  const rawHash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
  const hashParams = new URLSearchParams(rawHash);
  const get = (key) => hashParams.get(key) ?? url.searchParams.get(key);

  // `error_code` es el específico de Supabase (otp_expired, …); `error` es el genérico (access_denied).
  const errorCode = get("error_code") || get("error") || null;
  const hasToken = Boolean(get("access_token") || get("code"));

  return {
    isAuthCallback: hasToken || Boolean(errorCode),
    isRecovery: get("type") === "recovery",
    hasError: Boolean(errorCode),
    errorCode,
  };
}

/**
 * Borra del address bar cualquier resto del callback (tokens o errores) sin recargar ni navegar.
 * Evita que el token quede en el historial, en el `Referer` de pedidos posteriores o en una captura.
 */
export function scrubAuthCallbackFromUrl(win = typeof window !== "undefined" ? window : null) {
  if (!win?.location || !win?.history?.replaceState) return;
  const { pathname, hash, search } = win.location;
  if (!hash && !search) return;
  try {
    win.history.replaceState(win.history.state, "", pathname);
  } catch {
    // Un history bloqueado no debe romper el flujo de reset.
  }
}

/**
 * Crea la señal: captura el estado de la URL y se suscribe a `onAuthStateChange` para latchear
 * `PASSWORD_RECOVERY`. Idempotente por cliente (ver `installRecoverySignal`).
 */
export function createRecoverySignal({ client, href }) {
  const state = { ...parseAuthCallback(href), recoveryEvent: false };
  const listeners = new Set();

  const snapshot = () => ({ ...state });
  const emit = () => { for (const listener of listeners) listener(snapshot()); };

  let subscription = null;
  try {
    subscription = client?.auth?.onAuthStateChange?.((event) => {
      if (event !== "PASSWORD_RECOVERY") return;
      if (state.recoveryEvent) return;
      state.recoveryEvent = true;
      emit();
    })?.data?.subscription ?? null;
  } catch {
    // Sin listener la página sigue funcionando por la vía de los parámetros de la URL.
  }

  return {
    get: snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose() {
      listeners.clear();
      subscription?.unsubscribe?.();
    },
  };
}

let installed = null;

/** Instala la señal global (una sola vez). La llama `supabaseClient.js` al crear el cliente. */
export function installRecoverySignal(client, href) {
  if (installed) return installed;
  installed = createRecoverySignal({
    client,
    href: href ?? (typeof window !== "undefined" ? window.location.href : ""),
  });
  return installed;
}

/** Devuelve la señal instalada. `null` fuera del browser (SSR/tests que no la instalaron). */
export function getRecoverySignal() {
  return installed;
}

/** Sólo para tests: descarta la señal instalada. */
export function resetRecoverySignalForTests() {
  installed?.dispose();
  installed = null;
}
